// V1.0.64: Tìm kiếm trong transcript - FTS với unaccent + highlight
import { useState, useEffect } from 'react';
import {
  Input, Card, Select, DatePicker, Button, Tag, Empty, Spin,
  Row, Col, Pagination, Tooltip, Drawer, InputNumber, App as AntdApp,
} from 'antd';
import { Search as SearchIcon, Calendar, Filter, Play, Building2, Clock, Volume2, Download, Copy } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { voiceService, type VoiceSearchHit } from '@/services/voiceService';
import { departmentService } from '@/services/categoryService';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

export default function VoiceSearchPage() {
  const { message } = AntdApp.useApp();
  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [deviceIds, setDeviceIds] = useState<string[]>([]);
  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [hourFrom, setHourFrom] = useState<number | null>(null);
  const [hourTo, setHourTo] = useState<number | null>(null);
  const [minConfidence, setMinConfidence] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [playingHit, setPlayingHit] = useState<VoiceSearchHit | null>(null);
  const [audioBlobUrl, setAudioBlobUrl] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState(false);

  // Khi mở drawer "Nghe", fetch audio qua axios (có JWT)
  useEffect(() => {
    if (!playingHit) {
      if (audioBlobUrl) URL.revokeObjectURL(audioBlobUrl);
      setAudioBlobUrl(null);
      return;
    }
    let cancelled = false;
    setAudioLoading(true);
    voiceService.fetchAudioBlobUrl(playingHit.recordId)
      .then(url => {
        if (cancelled) {
          URL.revokeObjectURL(url);
        } else {
          if (audioBlobUrl) URL.revokeObjectURL(audioBlobUrl);
          setAudioBlobUrl(url);
        }
      })
      .catch(e => {
        if (!cancelled) message.error('Không tải được audio: ' + (e?.response?.data?.message ?? e.message));
      })
      .finally(() => { if (!cancelled) setAudioLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playingHit?.recordId]);

  const onDownloadHit = async () => {
    if (!playingHit) return;
    try {
      await voiceService.downloadAudio(playingHit.recordId);
      message.success('Đã tải file');
    } catch (e: any) {
      message.error('Tải file thất bại: ' + (e?.response?.data?.message ?? e.message));
    }
  };

  const onCopyHitText = () => {
    if (!playingHit) return;
    navigator.clipboard.writeText(playingHit.text);
    message.success('Đã copy đoạn text');
  };

  const { data: devices = [] } = useQuery({
    queryKey: ['recording-devices-for-search'],
    queryFn: () => voiceService.listDevices({ approved: true }),
  });

  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentService.list(),
  });

  const { data: searchResult, isLoading } = useQuery({
    queryKey: ['voice-search', submittedQuery, deviceIds, departmentIds, dateRange, hourFrom, hourTo, minConfidence, page],
    queryFn: () => voiceService.search({
      q: submittedQuery,
      deviceIds: deviceIds.length ? deviceIds : undefined,
      departmentIds: departmentIds.length ? departmentIds : undefined,
      from: dateRange?.[0]?.startOf('day').toISOString(),
      to: dateRange?.[1]?.endOf('day').toISOString(),
      hourFrom: hourFrom ?? undefined,
      hourTo: hourTo ?? undefined,
      minConfidence: minConfidence != null ? minConfidence / 100 : undefined,
      page,
      pageSize,
    }),
    enabled: !!submittedQuery,
  });

  const onSearch = () => {
    if (!query.trim()) {
      message.warning('Nhập từ khóa cần tìm');
      return;
    }
    setSubmittedQuery(query.trim());
    setPage(1);
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>
          <SearchIcon size={20} style={{ verticalAlign: -3, marginRight: 8, color: '#6699CC' }} />
          Tìm kiếm trong ghi âm
        </h2>
        <div style={{ color: '#7d8a98', fontSize: 13, marginTop: 4 }}>
          Tìm theo nội dung lời nói đã được Whisper transcribe. Hỗ trợ tìm không dấu (vd: "kham" → "khám").
        </div>
      </div>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]}>
          <Col span={24}>
            <Input.Search
              size="large"
              placeholder='Nhập từ khóa, vd: "khám lại sau 1 tuần", "khiếu nại", "đặt lịch"'
              value={query}
              onChange={e => setQuery(e.target.value)}
              onSearch={onSearch}
              enterButton="Tìm kiếm"
              prefix={<SearchIcon size={18} style={{ color: '#8a95a5' }} />}
            />
          </Col>

          <Col xs={24} md={12}>
            <div style={{ fontSize: 12, color: '#5b6875', marginBottom: 4 }}>🖥 Lọc theo máy:</div>
            <Select
              mode="multiple"
              allowClear
              placeholder="Tất cả thiết bị"
              style={{ width: '100%' }}
              value={deviceIds}
              onChange={setDeviceIds}
              optionFilterProp="label"
              options={devices.map(d => ({
                value: d.id,
                label: `${d.deviceCode} - ${d.deviceName}`,
              }))}
            />
          </Col>

          <Col xs={24} md={12}>
            <div style={{ fontSize: 12, color: '#5b6875', marginBottom: 4 }}>🏥 Lọc theo khoa:</div>
            <Select
              mode="multiple"
              allowClear
              placeholder="Tất cả khoa"
              style={{ width: '100%' }}
              value={departmentIds}
              onChange={setDepartmentIds}
              optionFilterProp="label"
              options={departments.map((d: any) => ({ value: d.id, label: d.name }))}
            />
          </Col>

          <Col xs={24} md={12}>
            <div style={{ fontSize: 12, color: '#5b6875', marginBottom: 4 }}>📅 Khoảng ngày:</div>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange as any}
              onChange={(d) => setDateRange(d as any)}
              format="DD/MM/YYYY"
            />
          </Col>

          <Col xs={12} md={6}>
            <div style={{ fontSize: 12, color: '#5b6875', marginBottom: 4 }}>🕐 Từ giờ:</div>
            <InputNumber
              min={0} max={23}
              placeholder="0"
              style={{ width: '100%' }}
              value={hourFrom}
              onChange={setHourFrom}
            />
          </Col>

          <Col xs={12} md={6}>
            <div style={{ fontSize: 12, color: '#5b6875', marginBottom: 4 }}>🕐 Đến giờ:</div>
            <InputNumber
              min={0} max={23}
              placeholder="23"
              style={{ width: '100%' }}
              value={hourTo}
              onChange={setHourTo}
            />
          </Col>

          <Col xs={12} md={6}>
            <div style={{ fontSize: 12, color: '#5b6875', marginBottom: 4 }}>📊 Độ tin cậy ≥ (%)</div>
            <InputNumber
              min={0} max={100}
              placeholder="50"
              style={{ width: '100%' }}
              value={minConfidence}
              onChange={setMinConfidence}
              suffix="%"
            />
          </Col>
        </Row>
      </Card>

      {!submittedQuery && (
        <Empty description="Nhập từ khóa và nhấn Tìm kiếm" />
      )}

      {isLoading && (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      )}

      {submittedQuery && searchResult && (
        <>
          <div style={{ marginBottom: 12, color: '#5b6875', fontSize: 13 }}>
            Tìm thấy <b>{searchResult.total}</b> kết quả cho "<b>{submittedQuery}</b>"
          </div>

          {searchResult.hits.length === 0 ? (
            <Empty description="Không có kết quả phù hợp" />
          ) : (
            <>
              {searchResult.hits.map(hit => (
                <Card
                  key={hit.segmentId}
                  size="small"
                  style={{ marginBottom: 8 }}
                  bodyStyle={{ padding: 12 }}
                  hoverable
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: 6, fontSize: 12, color: '#7d8a98' }}>
                        <span style={{ color: '#1976D2', fontFamily: 'monospace', fontWeight: 600 }}>
                          {hit.deviceCode}
                        </span>
                        {' · '}
                        <span>{hit.deviceName}</span>
                        {hit.departmentName && (
                          <>
                            {' · '}
                            <Building2 size={11} style={{ verticalAlign: -2 }} /> {hit.departmentName}
                          </>
                        )}
                      </div>

                      <div style={{ fontSize: 12, color: '#8a95a5', marginBottom: 6 }}>
                        <Calendar size={11} style={{ verticalAlign: -1 }} />{' '}
                        {dayjs(hit.absoluteTime).format('DD/MM/YYYY HH:mm:ss')}
                        {' · '}
                        <Clock size={11} style={{ verticalAlign: -1 }} />{' '}
                        Vị trí trong file: {Math.floor(hit.startSec / 60)}:{String(Math.floor(hit.startSec % 60)).padStart(2, '0')}
                        {hit.confidence != null && (
                          <>
                            {' · '}
                            <Tag style={{ fontSize: 10 }} color={hit.confidence > 0.7 ? 'green' : hit.confidence > 0.4 ? 'orange' : 'red'}>
                              Confidence: {(hit.confidence * 100).toFixed(0)}%
                            </Tag>
                          </>
                        )}
                      </div>

                      <div
                        style={{
                          padding: '8px 10px',
                          background: '#fafafa',
                          borderLeft: '3px solid #6699CC',
                          fontSize: 13,
                          lineHeight: 1.6,
                          borderRadius: '0 4px 4px 0',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: hit.snippet.replace(
                            /<mark>/g,
                            '<mark style="background: #fff7cc; padding: 1px 3px; border-radius: 2px; font-weight: 600;">'
                          ),
                        }}
                      />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <Tooltip title="Nghe đoạn này">
                        <Button
                          size="small"
                          type="primary"
                          icon={<Play size={14} />}
                          onClick={() => setPlayingHit(hit)}
                        >
                          Nghe
                        </Button>
                      </Tooltip>
                    </div>
                  </div>
                </Card>
              ))}

              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Pagination
                  current={page}
                  pageSize={pageSize}
                  total={searchResult.total}
                  onChange={setPage}
                  showSizeChanger={false}
                />
              </div>
            </>
          )}
        </>
      )}

      <Drawer
        title={playingHit ? `${playingHit.deviceCode} · ${dayjs(playingHit.absoluteTime).format('DD/MM/YYYY HH:mm:ss')}` : ''}
        open={!!playingHit}
        onClose={() => setPlayingHit(null)}
        width={560}
      >
        {playingHit && (
          <>
            <Card size="small" style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, marginBottom: 8 }}>
                <Volume2 size={14} style={{ verticalAlign: -2, marginRight: 6, color: '#1976D2' }} />
                <b>Phát đoạn ghi âm chứa từ khóa:</b>
              </div>
              {audioLoading ? (
                <div style={{ padding: 16, textAlign: 'center', background: '#fafafa' }}>
                  <Spin size="small" /> <span style={{ marginLeft: 8, color: '#7d8a98' }}>Đang tải audio...</span>
                </div>
              ) : audioBlobUrl ? (
                <audio
                  controls
                  autoPlay
                  style={{ width: '100%' }}
                  src={`${audioBlobUrl}#t=${Math.max(0, playingHit.startSec - 3)}`}
                />
              ) : (
                <Empty description="Không tải được audio" />
              )}
              <div style={{ fontSize: 11, color: '#8a95a5', marginTop: 6 }}>
                Player nhảy tới giây {Math.floor(Math.max(0, playingHit.startSec - 3))} (trước từ khóa 3s)
              </div>
              <div style={{ marginTop: 8, textAlign: 'right' }}>
                <Button
                  size="small"
                  icon={<Download size={14} />}
                  onClick={onDownloadHit}
                  disabled={!audioBlobUrl}
                >
                  Tải file audio
                </Button>
              </div>
            </Card>
            <Card
              size="small"
              title="Nội dung đầy đủ của đoạn"
              extra={
                <Button size="small" icon={<Copy size={12} />} onClick={onCopyHitText}>
                  Copy
                </Button>
              }
            >
              <div style={{ fontSize: 13, lineHeight: 1.6 }}>{playingHit.text}</div>
            </Card>
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Button
                onClick={() => window.open(`/voice-records/device/${playingHit.deviceCode}?date=${dayjs(playingHit.absoluteTime).format('YYYY-MM-DD')}`, '_blank')}
              >
                Mở timeline máy này (ngày đó)
              </Button>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}
